import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import liffManager from '../lib/liff';

/**
 * アプリケーション用のナビゲーションフック
 * LIFF環境での戻る挙動を考慮した統一的なナビゲーション機能を提供
 */
export const useAppNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * 戻るボタンの挙動を管理
   * LIFF環境で履歴がない場合は LIFF ウィンドウを閉じる
   */
  const goBack = useCallback(() => {
    const isLiff = liffManager.isInClient();
    const hasHistory = window.history.length > 1;

    if (isLiff && !hasHistory) {
      // LIFF環境で履歴がない場合は閉じる
      liffManager.closeWindow();
    } else {
      // 通常の戻る
      navigate(-1);
    }
  }, [navigate]);

  /**
   * ホームに戻る
   *
   * @param patientId 指定した場合、`?patient_id=xxx` を付与して遷移する。
   *   新規家族連携直後に呼び出すと、ホーム画面 (PatientContext) が
   *   index 0 ではなく新規利用者を初期選択する。
   */
  const goHome = useCallback((patientId?: number) => {
    const path = patientId != null ? `/?patient_id=${patientId}` : '/';
    navigate(path, { replace: true });
  }, [navigate]);

  /**
   * 指定したパスに遷移 (履歴を残す)
   */
  const goTo = useCallback((path: string, state?: any) => {
    navigate(path, { state });
  }, [navigate]);

  /**
   * 指定したパスに遷移 (履歴を置き換え)
   */
  const replaceTo = useCallback((path: string, state?: any) => {
    navigate(path, { replace: true, state });
  }, [navigate]);

  /**
   * 現在のパスが指定したパスと一致するかチェック
   */
  const isCurrentPath = useCallback((path: string) => {
    return location.pathname === path;
  }, [location.pathname]);

  return {
    goBack,
    goHome,
    goTo,
    replaceTo,
    isCurrentPath,
    navigate,
    location,
  };
};

export default useAppNavigation;
